import * as _ from 'lodash';
import moment from 'moment';
import { Component } from 'react';
import {
  Button,
  DropdownButton,
  MenuItem,
  Modal,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import type { RouteComponentProps } from 'react-router';

import { plural } from '../../../../shared/helpers/plural';
import { getReleaseGroupScope } from '../../../../shared/issuesV2';
import { Action } from '../../../../typings/permissions';
import { userHasPermissionForReleaseGroup } from '../../../helpers/permissions';
import { isDependencyCacheValid } from '../../../helpers/revisions';
import * as IssuesV2Store from '../../../redux/issuesV2';
import ProjectGroupsModule from '../../../redux/project_grouping';
import utils from '../../../utils';
import TooltipButton from '../../generic/Button/TooltipButton';
import Icon, { icons, IconText } from '../../generic/Icon';
import Link from '../../generic/Link/Link';
import Loader from '../../generic/Loader';
import { TeamAccessCard } from '../overview/ProjectSummaryV2/InfoCards/TeamAccessCard';
import type { IssueTypesSummary, Scope } from '../../../../shared/issuesV2';
import type { AsyncValue } from '../../../redux/asyncValue';
import type {
  APIError,
  IAsyncStatus,
  ProjectGroup,
  ProjectGroupRelease,
  ProjectGroupReleaseScan,
} from '../../../types';

import IssueListItem from './components/IssueListItem';
import { ProjectGroupReleaseSummaryActionCards } from './components/ProjectGroupReleaseSummaryActionCards';

type Props = {
  projectGroup: ProjectGroup;
  releaseScans: ProjectGroupReleaseScan[];
  releaseSummary: {
    project_count: number;
    dependency_count: number;
    license_count: number;
    isSteady: boolean;
  };
  loading: boolean;
  error: APIError;
  selectedRelease: ProjectGroupRelease;
  selectedScan?: ProjectGroupReleaseScan;
  scanStatus: IAsyncStatus;
  releaseSummaryStatus: IAsyncStatus;
  issueTypeSummary: AsyncValue<IssueTypesSummary>;
  issuesSortedByType: Array<[keyof IssueTypesSummary, number]> | null;
  typeSummaryTotalIssueCount: number | null;
} & RouteComponentProps<Params, {}> & DispatchProps;

type DispatchProps = {
  fetchReleaseScans: (projectGroupId: number, id: number) => Promise<void>;
  scanRelease: (projectGroupId: number, id: number) => Promise<void>;
  selectScan: (scanId: number | null) => void;
  selectIssuesScanId: (scanId?: number) => void;
  fetchIssueTypeSummary: (scope: Scope) => Promise<void>;
};

type Params = {
  projectGroupId: number;
};

type State = {
  showSuggestUpgradeModal: boolean;
};

export class ProjectGroupReleaseSummaryPage extends Component<Props, State> {
  readonly state = {
    showSuggestUpgradeModal: false,
  };

  async componentDidMount() {
    if (this.props.selectedRelease) {
      await this.fetchData(this.props.selectedRelease);
    }
  }

  async componentDidUpdate(prevProps) {
    if (!_.isEqual(prevProps.selectedRelease, this.props.selectedRelease)) {
      await this.fetchData(this.props.selectedRelease);
    } else if (this.props.selectedRelease && prevProps.selectedScan?.id !== this.props.selectedScan?.id) {
      if (this.props.selectedScan?.id) {
        this.applyScanSelectionSideEffects(this.props.selectedScan.id);
      }
      const scope = getReleaseGroupScope(
        this.props.selectedRelease.projectGroupId.toString(),
        this.props.selectedRelease.id,
        this.props.selectedScan?.id,
      );
      await this.props.fetchIssueTypeSummary(scope);
    }
  }

  async fetchData(release: ProjectGroupRelease) {
    const scope = getReleaseGroupScope(release.projectGroupId as string, release.id, this.props.selectedScan?.id);
    await this.props.fetchReleaseScans(this.props.params.projectGroupId, release.id);
    await this.props.fetchIssueTypeSummary(scope);

    if (!this.props.selectedScan?.id) {
      this.props.selectScan(_.maxBy(this.props.releaseScans, scan => Date.parse(scan.scanned_at))?.id);
    }
  }

  formatRevisionScanTimestamp = (timestamp) => moment(timestamp).format('MM/DD/YY h:mm a');

  renderRevisionScans = () => this.props.releaseScans.map(
    scan => (
      <MenuItem
        key={scan.id}
        active={scan.id === this.props.selectedScan?.id}
        eventKey={scan.id}
      >
        {this.formatRevisionScanTimestamp(scan.scanned_at)}
      </MenuItem>
    ),
  );

  onShowLimitedScanDetailInfo = () => {
    this.setState({ showSuggestUpgradeModal: true });
  };

  onHideLimitedScanDetailInfo = () => {
    this.setState({ showSuggestUpgradeModal: false });
  };

  onScanRelease = async () => {
    const { projectGroup, selectedRelease, scanRelease } = this.props;
    await scanRelease(projectGroup.id, selectedRelease.id);
  };

  get baseUrl() {
    return `/projects/group/${this.props.projectGroup.id}`;
  }

  applyScanSelectionSideEffects = (scanId: number) => {
    this.props.selectIssuesScanId(scanId);
    this.props.router.push({
      pathname: this.props.location.pathname,
      search: `?releaseScanId=${scanId}`,
    });
  };

  onScanSelect = (scanId: number) => {
    this.props.selectScan(scanId);
  };

  renderIssues({ dependencies, licenses }) {
    const isScanned = this.props.releaseScans.length > 0 ?? false;
    const { selectedScan: scan, issuesSortedByType, typeSummaryTotalIssueCount } = this.props;
    let issueCount = 0;
    let renderedIssues = [];

    if (scan && issuesSortedByType) {
      issueCount = typeSummaryTotalIssueCount || 0;
      renderedIssues = issuesSortedByType.map(([issueType, count]) => (
        <IssueListItem
          key={issueType}
          issueType={issueType}
          issuesCount={count}
          projectGroup={this.props.projectGroup}
          router={this.props.router}
        />
      ));
    }
    const btnLod = !utils.verify_subscription('premium') && (
      <Button
        className="btn btn-increase-detail btn-link"
        onClick={this.onShowLimitedScanDetailInfo}
      >
        <IconText
          icon={icons.warning}
          text="Scan depth is limited"
        />
      </Button>
    );
    const issueHeader = (
      <b className="inline-header">
        Issues in {this.props.selectedRelease?.title ?? '...'}
        {this.props.releaseScans.length > 0 && scan && (
          <span>
            <Icon icon={icons.angleRight} />
            <DropdownButton
              id="select-release-scan"
              className="dropdown-mid choose-scan inline-header"
              key={scan.id}
              title={`Scanned: ${this.formatRevisionScanTimestamp(scan.scanned_at)}`}
              onSelect={this.onScanSelect}
            >
              {this.renderRevisionScans()}
            </DropdownButton>
          </span>
        )}
      </b>
    );

    const canEditReleaseGroup = userHasPermissionForReleaseGroup(Action.Edit, this.props.projectGroup.id);
    const allDependencyCacheValid = this.props.selectedRelease?.projects?.every(
      project =>
        isDependencyCacheValid(project.revision),
    );
    if (!isScanned) {
      return (
        <div>
          <div className="project-summary-header">
            <div className="row">
              <div className="col-md-7">
                {issueHeader}
                <h4>Not Scanned Yet</h4>
              </div>
              <div className="col-md-5 text-right">
                {btnLod}
              </div>
            </div>
          </div>
          <div className="text-center margin-top">
            <div className="card noborder margin-top">
              <br />
              <span className="margin-top" style={{ fontSize: '65px' }}>
                <Icon icon={icons.cube} />
              </span>
              <h3>Ready to Scan</h3>
              <p>
                FOSSA has discovered <b>{licenses || '...'}</b> Licenses and<br />
                <b>{dependencies || '...'}</b> Dependencies in this version.
              </p>
              <TooltipScanButton
                onScanRelease={this.onScanRelease}
                canEditReleaseGroup={canEditReleaseGroup}
                allDependencyCacheValid={allDependencyCacheValid}
                />
            </div>
          </div>
        </div>
      );
    }

    const lastScan = _.head(this.props.releaseScans);
    let btnScan;
    if (canEditReleaseGroup && allDependencyCacheValid) {
      btnScan = (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id="lastScanTooltip">
              <b>Last policy scan:</b> {lastScan ? moment(lastScan.scanned_at).fromNow() : 'Unknown'}
            </Tooltip>
          }>
          <a className="clickable btn-scan" onClick={this.onScanRelease}>
            <IconText
              icon={icons.reload}
              text="Run policy scan"
            />
          </a>
        </OverlayTrigger>
      );
    } else {
      const tooltipMessage = allDependencyCacheValid ? 'You are not permitted to edit this release group.' : 'FOSSA is still determining the dependencies for this revision';
      btnScan = (
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id="cannotScan">
              {tooltipMessage}
            </Tooltip>
          }>
          <a className="clickable btn-scan">
            <IconText
              icon={icons.reload}
              text="Run policy scan"
            />
          </a>
        </OverlayTrigger>
      );
    }

    return issueCount > 0 ? (
      <div>
        <div className="project-summary-header project-summary-failed">
          <div className="row">
            <div className="col-md-7">
              {issueHeader}
              <h4>
                {issueCount} {plural(issueCount, 'Issue')}
                {btnScan}
              </h4>
            </div>
            <div className="col-md-5 text-right">
              {btnLod}
              {/* TODO: add icons for "license protection", "security", etc... "ENABLED" */}
            </div>
          </div>
        </div>
        <div className="padded padded-small">
          <ul className="project-issues-list">{renderedIssues}</ul>
        </div>
      </div>
    ) : (
      <div>
        <div className="project-summary-header project-summary-success">
          <div className="row">
            <div className="col-md-7">
              {issueHeader}
              <h4>
                <IconText
                  icon={icons.check}
                  text="All Checks Passed"
                />
                {btnScan}
              </h4>
            </div>
            <div className="col-md-5 text-right">
              {btnLod}
            </div>
          </div>
        </div>
        <div className="text-center card card-success noborder margin-top">
          <br />
          <span className="margin-top" style={{ fontSize: '65px' }}>
            <Icon icon={icons.review} />
          </span>
          <br />
          <br />
          <p className="subtitle">
            FOSSA ran license checks across <b>{dependencies}</b> dependencies.
            {issueCount === 0 && (
              <div>No issues were found.</div>
            )}
          </p>
          <Button
            className="clickable"
            href="/reports"
          >
            <IconText
              icon={icons.addToProject}
              text="Generate Report"
            />
          </Button>
        </div>
      </div>
    );
  }

  render() {
    if (this.props.scanStatus.loading || this.props.issueTypeSummary.isLoading) {
      return <Loader />;
    }
    const baseUrl = this.baseUrl;
    const stats = this.props.releaseSummary;
    return (
      <div className="container margin-top">
        {this.props.scanStatus.error?.message && (
          <div role="alert" className="alert alert-error alert-dismissable">
            Error scanning release: {this.props.scanStatus.error.message}
          </div>
        )}
        {this.props.releaseSummaryStatus.error?.message && (
          <div role="alert" className="alert alert-error alert-dismissable">
            Error retrieving release summary: {this.props.releaseSummaryStatus.error.message}
          </div>
        )}
        <div className="animated fadeInUp quick project_info">
          <div className="row project_info__container">
            <div className="col-md-8 project_info__summary">
              <div className="panel panel-flat project_info__issues_panel">
                {this.renderIssues(stats)}
              </div>
            </div>
            <div className="col-md-4 project_info__action_cards">
              <ProjectGroupReleaseSummaryActionCards
                baseUrl={baseUrl}
                projectGroupId={this.props.projectGroup.id}
                selectedReleaseId={this.props.selectedRelease.id}
                projectCount={stats.project_count}
                dependencyCount={stats.dependency_count}
                licenseCount={stats.license_count}
                isReleaseSummaryStatusLoading={this.props.releaseSummaryStatus.loading}
              />
              <TeamAccessCard
                releaseGroupId={this.props.projectGroup.id}
                isLoading={this.props.releaseSummaryStatus.loading}
              />
            </div>
          </div>
        </div>
        <Modal show={this.state.showSuggestUpgradeModal} backdrop onHide={this.onHideLimitedScanDetailInfo}>
          <Modal.Header closeButton>
            <Modal.Title>Scan depth for this project is limited</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Upgrade to the business plan to unlock additional features.</p>
            <Link to="/account/settings/billing" className="btn btn-primary margin-bottom-sm">Subscription Settings
            </Link>
          </Modal.Body>
        </Modal>
      </div>
    );
  }
}

const TooltipScanButton = ({ onScanRelease, canEditReleaseGroup, allDependencyCacheValid }) => {
  const isDisabled = !canEditReleaseGroup || !allDependencyCacheValid;
  const tooltipMessage = !canEditReleaseGroup
    ? 'You are not permitted to edit this release group.'
    : 'FOSSA is still determining dependencies for one of the projects.';
  return (
    <TooltipButton
      onClick={onScanRelease}
      tooltip={tooltipMessage}
      showTooltip={isDisabled}
      isDisabled={isDisabled}
    >
      <IconText
        icon={icons.reload}
        text="Run Issue Scan"
      />
    </TooltipButton>
  );
};

const mapStateToProps = createStructuredSelector({
  releaseSummary: ProjectGroupsModule.selectors.getReleaseSummary,
  releaseSummaryStatus: ProjectGroupsModule.selectors.getReleaseSummaryStatus,
  releaseScans: ProjectGroupsModule.selectors.getReleaseScans,
  scanStatus: ProjectGroupsModule.selectors.getReleaseScanStatus,
  selectedRelease: ProjectGroupsModule.selectors.getSelectedRelease,
  selectedScan: ProjectGroupsModule.selectors.getSelectedScan,
  issueTypeSummary: IssuesV2Store.selectors.getIssueTypeSummary,
  issuesSortedByType: IssuesV2Store.selectors.getSortedIssueTypeSummary,
  typeSummaryTotalIssueCount: IssuesV2Store.selectors.getTotalIssuesFromTypeSummary,
});

const mapDispatchToProps = {
  scanRelease: ProjectGroupsModule.actions.scanRelease,
  fetchReleaseScans: ProjectGroupsModule.actions.fetchReleaseScans,
  selectScan: ProjectGroupsModule.actions.selectScan,
  selectIssuesScanId: IssuesV2Store.actions.setScanId,
  fetchIssueTypeSummary: IssuesV2Store.actions.fetchIssueTypeSummary,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProjectGroupReleaseSummaryPage);
